<template>
    <div class="container-fluid">
        <PageHeader :page="page" >
            <div class="d-flex justify-content-end h-100 align-items-end">
                <ActionButton
                    text="Regresar"
                    class="m-2"
                    i="fas fa-arrow-circle-left"
                    variant="outline-success"
                    @click="$router.go(-1)"
                />
                <ActionButton
                    text="Someter"
                    class="m-2"
                    i="fas fa-flag"
                    variant="outline-success"
                    @click="handleSubmit"
                />
                <ActionButton
                    text="Guardar"
                    class="m-2"
                    i="fas fa-save"
                    variant="success"
                    @click="handleSave"
                />
            </div>
        </PageHeader>

        <ValidationObserver ref="request-form">
            <RequestTypeSelector
                v-model="requestTypeSelected"
                :options="requestTypeOptions"
            />

            <RequestTypeDisclaimer
                :options="requestTypeOptions"
            />

            <div>
                <div class="section-title__container">
                    <span class="dot-icon text-center mr-2">
                        <i id="icon-info-circle" class="fas fa-info-circle" />
                    </span>
                    <b>Listado de estudiantes pendientes a solicitar recursos</b>
                </div>

                <div class="search-filter__container">
                    <CustomInput
                        left-icon
                        v-model="searchText"
                        style="min-width: 400px;"
                        icon="fas fa-search orange-icon"
                        placeholder="Escriba Número de SIE o Nombre del Estudiante"
                    />
                    <ActionButton
                        text="Buscar"
                        class="ml-3"
                        variant="success"
                        i="fas fa-search"
                        @click="applyFilter"
                    />
                </div>

                <div class="result__container">
                    <VacantPositionResult
                        v-for="vacantPosition in filteredResult"
                        :key="`${vacantPosition.saeE10Id}-${requestTypeSelected.id}`"
                        :value="vacantPosition"
                        :disabled="isSelectionDisabled && !vacantPosition.selected"
                        :collections="collections"
                        :require-documents="!isGroupRequest"
                        :custodian-students="custodianStudent"
                        @toggle-select="handleToggleSelection"
                        @custodian-redirect="custodianRedirect"
                    />
                    <custom-result-not-found
                        v-if="!filteredResult.length"
                        text="No cuenta con más SAEE10 para solicitar recurso."
                    />
                </div>

                <div v-if="isGroupRequest">
                    <div class="section-title__container">
                        <span class="dot-icon text-center mr-2">
                            <i id="icon-user-tie" class="fas fa-user-tie" />
                        </span>
                        <b>Listado de Maestros</b>
                    </div>
                    <div class="search-filter__container">
                        <validation-provider
                            mode="lazy"
                            v-slot="{ errors }"
                            rules="required"
                        >
                            <custom-drop-down
                                id="custodian"
                                style="min-width: 400px"
                                v-model="custodian"
                                label="Seleccione Maestro a asignar:"
                                fieldkey="id"
                                fieldname="name"
                                placeholder="Seleccione"
                                :error="errors[0]"
                                custom-error-msg="Favor seleccionar un Maestro"
                                :options="collections.custodians"
                                :clearable="false"
                            />
                        </validation-provider>
                    </div>
                </div>
            </div>
        </ValidationObserver>
    </div>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import CustomInput from 'CustomInput';
import formDataUtil from "@/utils/formDataUtil";
import documentsService from "@/services/documents";
import { getNecessitiesList } from "@/modules/human-resources/api";
import CustomDropDown from "custom-drop-down";
import CustomResultNotFound from 'custom-result-not-found';
import RequestTypeSelector from './RequestTypeSelector.vue';
import RequestTypeDisclaimer from './RequestTypeDisclaimer.vue';
import VacantPositionResult from './VacantPositionResult.vue';
import {
    GetRequestData,
    UpdateRequest,
    SubmitRequest,
    CreateRequestDocuments
} from '@/modules/peiSAEE/api.js';
import { getStudentParams } from "@/modules/human-resources/api";
import { requestTypeCodes } from "@/utils/constants/hrrequest/requestTypeCodes";
import mixAlert from "@/mixin/alert.mixin";

import requestTypeRadioOptions from "./RequestTypeRadioOptions";
const SUCCESS_MESSAGE = "La información ha sido guardada de manera exitosa.";
const HAVE_PENDING_CHANGES_MESSAGE = "No es posible redirigirlo a la pantalla, ya que se encontraron datos nuevos. Favor de guardar.";

export default {
    mixins: [mixAlert],
    components: {
        PageHeader,
        ActionButton,
        CustomInput,
        CustomDropDown,
        CustomResultNotFound,
        RequestTypeSelector,
        RequestTypeDisclaimer,
        VacantPositionResult,
    },
    data: () => ({
        page: {
            icon: 'fas fa-users-class py-1',
            title: 'Edicion de Solicitud de Persona Irregular',
            routes: ['Inicio', 'Recursos Humanos', 'Persona Irregular', 'Editar'],
        },
        requestTypesLimits: {
            studentMax: 0,
            studentMin: 0,
            groupMax: 0,
            groupMin: 0,
            twoOrMoreMax: 0,
            twoOrMoreMin: 0,
        },
        requestTypeSelected: null,
        requestTypeOptions: requestTypeRadioOptions,
        result: [],
        filteredResult: [],
        searchText: null,
        custodian: null,
        collections: {
          documentTypes: [],
          necessities: [],
          custodians: []
        },
        originalData: null
    }),
    computed: {
        custodianStudent() {
            return this.requestTypeSelected?.id == requestTypeCodes.Grupo ? this.custodian?.students : null;
        },
        isSelectionDisabled() {
            if (!this.requestTypeSelected) return true;

            const countSelected = this.result.filter(x => x.selected).length;
            switch (this.requestTypeSelected.id) {
                case requestTypeCodes.Estudiantes: return countSelected >= this.requestTypesLimits.studentMax;
                case requestTypeCodes.DosMasEstudiantes: return countSelected >= this.requestTypesLimits.twoOrMoreMax;
                case requestTypeCodes.Grupo: return countSelected >= this.requestTypesLimits.groupMax;
            }
        },
        isGroupRequest() {
            return this.requestTypeSelected?.id == requestTypeCodes.Grupo;
        },
        saee10IdsSelected() {
            return this.result
                .filter(x => x.selected)
                .map(x => x.saeE10Id);
        },
        saee10Documents() {
            return this.result.flatMap(x => {
              return x.documents.map(document => ({
                studentSieId: x.studentSieId,
                necessityId: document.necessityId,
                deleted: !x.selected,
                document
              }));
            });
        },
        hasPendingChanges() {
            const hasCustodianChange = this.originalData?.custodian?.id != this.custodian?.id;
            const hasRequestTypeCheck = this.originalData?.requestTypeId != this.requestTypeSelected.id;
            const hasSAEE10SelectionChange = this.originalData.saee10Ids.toString() != this.saee10IdsSelected.toString();
            
            return hasCustodianChange || hasRequestTypeCheck || hasSAEE10SelectionChange;
        }
    },
    methods: {
        async fetchResources() {
            const documentResponse = await documentsService.getDocumentTypes("HRRequest");
            this.collections.documentTypes = documentResponse.data;

            const necesitiesResponse = await getNecessitiesList();
            this.collections.necessities = necesitiesResponse.data;

            const { data } = await getStudentParams();
            this.requestTypesLimits = {
                studentMax: 1,
                studentMin: 1,
                groupMax: data.requestPersonalGroupMax,
                groupMin: data.requestPersonalGroupMin,
                twoOrMoreMax: data.requestPersonalTwoOrMoreMax,
                twoOrMoreMin: data.requestPersonalTwoOrMoreMin,
            };
        },
        async fetchRequestData() {
            const { data } = await GetRequestData(this.$route.params.id);
            this.originalData = {...data};
            this.requestTypeSelected = requestTypeRadioOptions.find(x => x.id == data.requestTypeId);

            this.setRequestState(data);
        },
        setRequestState(payload) {
            const data = {...payload};
            /*ATTACH DOCUMENTS*/
            const saee10AndDocuments = data.saee10s?.map(x => {
                const studentNeccesityIds = x.necesities?.map(x => x.value);
                const documents = data.documents.filter(doc =>
                    doc.studentSieId == x.studentSieId &&
                    studentNeccesityIds.includes(doc.necessityId)
                );
                const documentsDetail = documents?.map(x => {
                    const necesity = this.collections.necessities.find(n => n.id == x.necessityId);

                    return {
                        ...x,
                        necesityName: necesity.name,
                        necessityId: necesity.id
                    };
                });

                return {
                    ...x,
                    documents: documentsDetail,
                };
            });

            this.result = saee10AndDocuments;
            this.searchText = null,
            this.filteredResult = saee10AndDocuments;

            this.custodian = data.custodian;
            this.collections.custodians = data.custodians;
        },
        async handleSubmit() {
            try {
                if (!this.saee10IdsSelected.length) return;

                const isValidRequest = await this.$refs["request-form"].validate();
                if (!isValidRequest) {
                    this.ShowToast(
                        'Error',
                        'Favor de completar la información solicitada.',
                        'error'
                    );
                    return;
                }

                const formData = new FormData();
                formDataUtil.build(formData, this.saee10IdsSelected, "request.saee10Ids");
                formDataUtil.build(formData, this.requestTypeSelected.id, "request.type");
                formDataUtil.build(formData, this.custodian?.id, "request.custodianId");                                          
                formDataUtil.build(formData, this.saee10Documents, "documents.requestDocuments");            

                await SubmitRequest(this.$route.params.id, formData);
                this.ShowToast('', SUCCESS_MESSAGE, 'success');
                await this.fetchResources();    
                await this.fetchRequestData();
            } catch (error) {
              const message =
                  error?.response?.data?.message ??
                  "Error al someter la solicitud";

              this.ShowToast("", message, "error");
          }                                        
        },
        async handleSave() {
            try {
                if (!this.saee10IdsSelected.length) return;

                const request = {
                    saee10Ids: this.saee10IdsSelected,
                    type: this.requestTypeSelected.id,
                    custodianId: this.custodian?.id
                }

                await UpdateRequest(this.$route.params.id, request);

                if (this.saee10Documents.length) {
                    const formData = new FormData();
                    formDataUtil.build(formData, this.$route.params.id, "requestId");
                    formDataUtil.build(formData, this.saee10Documents, "requestDocuments");
                    await CreateRequestDocuments(formData);
                }

                this.ShowToast('', SUCCESS_MESSAGE, 'success');

                this.originalData = {
                    custodian: this.custodian,
                    saee10Ids: this.saee10IdsSelected,
                    requestTypeId: this.requestTypeSelected.id
                };
            } 
            catch (error) {
              const message =
                  error?.response?.data?.message ??
                  "Error al crear la solicitud";

                this.ShowToast("", message, "error");
            }
        },
        applyFilter() {
            if (!this.searchText) {
                this.filteredResult = this.result;
                return;
            }

            this.filteredResult = this.result.filter(
                item => `${item.studentSieId} - ${item.studentName}`.includes(this.searchText)
            );
        },
        handleToggleSelection(saeE10Id, value) {
            const vacantPositionResult = this.result.find(x => x.saeE10Id == saeE10Id);
            vacantPositionResult.selected = value;
        },
        custodianRedirect() {
            if (this.hasPendingChanges) {
                this.ShowToast("", HAVE_PENDING_CHANGES_MESSAGE, "error");
                return;
            }

            this.$router.push({ path: '/humanres/locationalternativemanagement' });
        },
    },
    watch: {
        requestTypeSelected: {
            deep: true,
            handler() {
                this.setRequestState(this.originalData);
            },
        },
    },
    async mounted() {
        await this.fetchResources();
        await this.fetchRequestData();
    },
};
</script>

<style scoped>
@import './common.scss';
</style>
